import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import { usePresetsStore } from '@/stores/presets';
import { useInitializeAuth } from '@/hooks/useAuth';
import { Toaster } from '@/components/ui/sonner';

// Layouts
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ContentLayout } from '@/layouts/ContentLayout';

// Pages
import { LoginPage, ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth';
import { DashboardPage } from '@/pages/DashboardPage';
import { ContentPage, ItemPage, CollectionOrItemPage } from '@/pages/content';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { FilesPage, FileDetailPage } from '@/pages/files';
import { UsersPage, UserDetailPage } from '@/pages/users';
import { ProfilePage } from '@/pages/profile/ProfilePage';

// Create query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
        },
    },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, accessToken, appAccess } = useAuthStore();

    // If we have tokens stored but haven't verified yet, show loading
    // This prevents flash redirect during auth initialization
    if (accessToken && !isAuthenticated) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
        );
    }

    if (!isAuthenticated && !accessToken) {
        return <Navigate to="/auth/login" replace />;
    }

    // Check if user has app access
    if (isAuthenticated && !appAccess) {
        return (
            <div className="flex h-screen items-center justify-center bg-background">
                <div className="text-center p-8 max-w-md">
                    <div className="mb-4">
                        <svg className="mx-auto h-16 w-16 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-semibold text-foreground mb-2">No App Access</h1>
                    <p className="text-muted-foreground mb-6">
                        You don't have permission to access the application. Please contact your administrator to request access.
                    </p>
                    <button
                        onClick={() => useAuthStore.getState().logout()}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}

// Component for admin-only routes
function AdminRoute({ children }: { children: React.ReactNode }) {
    const { adminAccess } = useAuthStore();

    if (!adminAccess) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-center p-8 max-w-md">
                    <div className="mb-4">
                        <svg className="mx-auto h-16 w-16 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-semibold text-foreground mb-2">Access Restricted</h1>
                    <p className="text-muted-foreground">
                        You need administrator privileges to access this section.
                    </p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
    const { isLoading } = useInitializeAuth();
    const { isAuthenticated } = useAuthStore();
    const { hydrate: hydratePresets, hydrated: presetsHydrated } = usePresetsStore();

    // Hydrate presets after auth is initialized and user is authenticated
    useEffect(() => {
        if (!isLoading && isAuthenticated && !presetsHydrated) {
            hydratePresets();
        }
    }, [isLoading, isAuthenticated, presetsHydrated, hydratePresets]);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            </div>
        );
    }

    return <>{children}</>;
}

function AppRoutes() {
    return (
        <AuthInitializer>
            <Routes>
                {/* Auth routes */}
                <Route element={<AuthLayout />}>
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                </Route>

                {/* App routes */}
                <Route
                    element={
                        <ProtectedRoute>
                            <AppLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="/" element={<Navigate to="/admin" replace />} />
                    <Route path="/admin" element={<DashboardPage />} />

                    {/* Content routes with nested layout */}
                    <Route path="/admin/content" element={<ContentLayout />}>
                        <Route index element={<ContentPage />} />
                        <Route path=":collection" element={<CollectionOrItemPage />} />
                        <Route path=":collection/:id" element={<ItemPage />} />
                    </Route>

                    {/* Files routes - order matters: specific routes before generic ones */}
                    <Route path="/admin/files" element={<FilesPage />} />
                    <Route path="/admin/files/all" element={<FilesPage />} />
                    <Route path="/admin/files/mine" element={<FilesPage />} />
                    <Route path="/admin/files/recent" element={<FilesPage />} />
                    <Route path="/admin/files/folders/:folderId" element={<FilesPage />} />
                    <Route path="/admin/files/:fileId" element={<FileDetailPage />} />
                    <Route path="/admin/users" element={<UsersPage />} />
                    <Route path="/admin/users/roles/:roleId" element={<UsersPage />} />
                    <Route path="/admin/users/new" element={<UserDetailPage />} />
                    <Route path="/admin/users/:userId" element={<UserDetailPage />} />


                    <Route path="/admin/profile" element={<ProfilePage />} />

                    <Route path="/admin/settings/*" element={<AdminRoute><SettingsPage /></AdminRoute>} />
                </Route>


                {/* Fallback */}
                <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </Routes>
        </AuthInitializer>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AppRoutes />
                <Toaster richColors position="top-right" />
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;
